<template>
			<div class="chat-group-side">
			<chat-group-member v-if="showAllMembers" @back="showAllMembers = false" :group="group"
				:groupMembers="showMembers" :group-id="groupId"></chat-group-member>
				<div v-else-if="hasGroup" class="group-side-content">
					<div class="group-overview-card">
						<div class="group-overview-main">
							<file-upload
								v-if="canEditGroupProfile"
								class="group-avatar-upload"
								:action="imageAction"
								:isPermanent="true"
								:showLoading="true"
								:disabled="isInteractionLocked"
								:maxSize="maxSize"
								:fileTypes="['image/jpeg', 'image/png', 'image/jpg', 'image/webp']"
								@success="onUploadSuccess"
							>
								<head-image class="group-overview-avatar" :name="groupDisplayName" :url="group.headImageThumb" :size="44" variant="group"
									radius="20%"></head-image>
								<span class="group-avatar-upload__mask">更换</span>
							</file-upload>
							<head-image v-else class="group-overview-avatar" :name="groupDisplayName" :url="group.headImageThumb" :size="44" variant="group"
								radius="20%"></head-image>
							<div class="group-overview-body">
									<div class="group-overview-title-row">
										<div class="group-overview-title" :title="groupDisplayName">{{ groupDisplayName }}</div>
										<div class="group-role-badge">{{ roleLabel }}</div>
									</div>
									<div class="group-overview-subtitle">{{ groupSummaryText }}</div>
									<div class="group-overview-status" :class="{ 'is-idle': !groupLoading && !groupLoadError, 'is-error': groupLoadError }">
										<template v-if="groupLoading">
											<i class="el-icon-loading"></i>
											<span>正在同步群资料</span>
										</template>
										<template v-else-if="groupLoadError">
										<i class="el-icon-warning-outline"></i>
										<span>{{ groupLoadErrorMessage }}</span>
										<button type="button" title="重试" @click="retryGroupLoad">
											<i class="el-icon-refresh"></i>
										</button>
										</template>
									</div>
							</div>
						</div>
					</div>
			<div class="member-area">
				<div class="member-header">
					<div class="member-title">群成员</div>
					<div class="more-member-btn" @click="onShowMoreMember">
						<span>全部</span>
						<i class="el-icon-arrow-right"></i>
					</div>
				</div>
				<div class="member-items">
					<div v-for="member in showGridMembers" :key="member.userId" class="member-entry">
						<group-member-item class="member-item" :group="group" :groupMembers="resolvedGroupMembers"
							:member="member" type="card"></group-member-item>
					</div>
					<div class="member-tools" :class="{ 'is-disabled': pendingAction || groupLoading }" v-if="isAllowInvite">
						<div class="tool-btn" title="邀请好友进群聊" @click="onInvite()">
							<i class="el-icon-plus"></i>
						</div>
						<div class="tool-text">邀</div>
						<add-group-member ref="addGroupMember" :groupId="group.id" :members="resolvedGroupMembers"
							@reload="$emit('reload')"></add-group-member>
					</div>
					<div class="member-tools" :class="{ 'is-disabled': pendingAction || groupLoading }" v-if="isOwner || isManager">
						<div class="tool-btn" title="选择成员移出群聊" @click="onRemove()">
							<i class="el-icon-minus"></i>
						</div>
						<div class="tool-text">移</div>
						<group-member-selector ref="removeSelector" title="选择成员进行移除" :group="group"
							@complete="onRemoveComplete"></group-member-selector>
					</div>
					<div class="member-tools" :class="{ 'is-disabled': pendingAction || groupLoading }" v-if="isOwner || isManager">
						<div class="tool-btn" title="禁言" @click="onMuted()">
							<span class="icon iconfont icon-chat-muted"></span>
						</div>
						<div class="tool-text">禁</div>
						<group-member-selector ref="mutedSelector" title="选择成员进行禁言" :group="group"
							@complete="onMutedComplete"></group-member-selector>
					</div>
					<div class="member-tools" :class="{ 'is-disabled': pendingAction || groupLoading }" v-if="isOwner || isManager">
						<div class="tool-btn" title="解除禁言" @click="onUnmuted()">
							<span class="icon iconfont icon-chat-unmuted"></span>
						</div>
						<div class="tool-text">解</div>
						<group-member-selector ref="unmutedSelector" title="选择成员进行解除禁言" :group="group"
							@complete="onUnmutedComplete"></group-member-selector>
					</div>
				</div>
			</div>

			<div class="group-info-section">
				<!-- 群聊名称 -->
				<div class="info-item">
					<div class="info-label">
						<i class="el-icon-collection-tag"></i>
						<span>群名</span>
					</div>
					<div class="info-content">
							<el-input v-if="isEditingGroupName" v-model="groupNameValue" size="small" placeholder="请输入群聊名称"
								:disabled="isInteractionLocked"
								maxlength="20" @blur="saveGroupName" @keyup.enter.native="saveGroupName"
								ref="groupNameInput">
							</el-input>
							<div v-else class="info-display" :class="{ 'info-display--readonly': !canEditGroupProfile || pendingAction || groupLoading }" @click="startEditGroupName">
							<span v-if="group.name" :title="group.name">{{ group.name }}</span>
							<span v-else class="info-placeholder">未设置</span>
							<i v-if="canEditGroupProfile" class="el-icon-edit-outline"></i>
						</div>
					</div>
				</div>

				<!-- 备注 -->
				<div class="info-item">
					<div class="info-label">
						<i class="el-icon-edit-outline"></i>
						<span>备注</span>
					</div>
					<div class="info-content">
							<el-input v-if="isEditingRemark" v-model="remarkValue" size="small" maxlength="32" :disabled="isInteractionLocked"
								@blur="saveRemark" @keyup.enter.native="saveRemark" ref="remarkInput">
							</el-input>
							<div v-else class="info-display" :class="{ 'info-display--readonly': pendingAction || groupLoading }" @click="startEditRemark">
							<span v-if="group.remarkGroupName" :title="group.remarkGroupName">{{ group.remarkGroupName }}</span>
							<span v-else class="info-placeholder">未设置</span>
							<i class="el-icon-edit-outline"></i>
						</div>
					</div>
				</div>

				<!-- 我在本群的昵称 -->
				<div class="info-item">
					<div class="info-label">
						<i class="el-icon-user"></i>
						<span>昵称</span>
					</div>
					<div class="info-content">
							<el-input v-if="isEditingNickName" v-model="nickNameValue" size="small" placeholder="请输入昵称"
								:disabled="isInteractionLocked"
								maxlength="20" @blur="saveNickName" @keyup.enter.native="saveNickName"
								ref="nickNameInput">
							</el-input>
							<div v-else class="info-display" :class="{ 'info-display--readonly': pendingAction || groupLoading }" @click="startEditNickName">
							<span v-if="group.remarkNickName" :title="group.remarkNickName">{{ group.remarkNickName }}</span>
							<span v-else class="info-placeholder">未设置</span>
							<i class="el-icon-edit-outline"></i>
						</div>
					</div>
				</div>
			</div>

			<!-- 群公告 -->
			<div class="notice-section" v-if="group.notice || isOwner || isManager">
				<div class="notice-header">
					<i class="el-icon-chat-line-square"></i>
					<span>群公告</span>
				</div>
				<div class="notice-content">
						<div class="notice-display" :class="{ 'notice-display--readonly': !canEditGroupProfile || pendingAction || groupLoading }" @click="openNoticeDialog">
						<div v-if="group.notice" class="notice-text">{{ group.notice }}</div>
						<div v-else class="notice-placeholder">暂无公告</div>
					</div>
				</div>
			</div>

			<!-- 会话设置 -->
			<div class="switch-setting">
				<div class="switch-item">
					<div class="label">
						<i class="el-icon-bell"></i>
						<span>免打扰</span>
					</div>
					<chat-setting-switch v-model="isDnd" :pending="pendingSettingKey === 'dnd'"
						:blocked="Boolean(pendingSettingKey)" :disabled="settingsDisabled"
						@change="onDndChange"></chat-setting-switch>
				</div>
				<div class="switch-item">
					<div class="label">
						<i class="el-icon-top"></i>
						<span>置顶</span>
					</div>
					<chat-setting-switch v-model="isTop" :pending="pendingSettingKey === 'top'"
						:blocked="Boolean(pendingSettingKey)" :disabled="settingsDisabled"
						@change="onTopChange"></chat-setting-switch>
				</div>
			</div>

			<!-- 群组权限配置 -->
			<div class="switch-setting" v-if="isOwner || isManager">
				<div class="switch-item">
					<div class="label">
						<i class="el-icon-warning-outline"></i>
						<span>全员禁言</span>
					</div>
					<chat-setting-switch v-model="group.isAllMuted" :pending="pendingSettingKey === 'allMuted'"
						:blocked="Boolean(pendingSettingKey)" :disabled="settingsDisabled"
						@change="onAllMutedChange"></chat-setting-switch>
				</div>
				<div class="switch-item">
					<div class="label">
						<i class="el-icon-user"></i>
						<span>成员可邀人</span>
					</div>
					<chat-setting-switch v-model="group.isAllowInvite" :pending="pendingSettingKey === 'allowInvite'"
						:blocked="Boolean(pendingSettingKey)" :disabled="settingsDisabled"
						@change="onAllowInviteChange"></chat-setting-switch>
				</div>
				<div class="switch-item">
					<div class="label">
						<i class="el-icon-share"></i>
						<span>成员可分享</span>
					</div>
					<chat-setting-switch v-model="group.isAllowShareCard" :pending="pendingSettingKey === 'allowShareCard'"
						:blocked="Boolean(pendingSettingKey)" :disabled="settingsDisabled"
						@change="onAllowShareCardChange"></chat-setting-switch>
				</div>
			</div>

			<div class="switch-setting" v-if="isOwner || isManager">
				<div class="switch-item">
					<div class="label">
						<i class="el-icon-user-solid"></i>
						<span>成员资料</span>
					</div>
					<chat-setting-switch v-model="group.isAllowViewMemberProfile" :pending="pendingSettingKey === 'viewMemberProfile'"
						:blocked="Boolean(pendingSettingKey)" :disabled="settingsDisabled"
						@change="onViewMemberProfileChange"></chat-setting-switch>
				</div>
				<div class="switch-item">
					<div class="label">
						<i class="el-icon-view"></i>
						<span>在线数</span>
					</div>
					<chat-setting-switch v-model="group.isAllowViewOnlineCount" :pending="pendingSettingKey === 'viewOnlineCount'"
						:blocked="Boolean(pendingSettingKey)" :disabled="settingsDisabled"
						@change="onViewOnlineCountChange"></chat-setting-switch>
				</div>
				<div class="switch-item">
					<div class="label">
						<i class="el-icon-time"></i>
						<span>历史</span>
					</div>
					<chat-setting-switch v-model="group.isAllowViewHistory" :pending="pendingSettingKey === 'viewHistory'"
						:blocked="Boolean(pendingSettingKey)" :disabled="settingsDisabled"
						@change="onViewHistoryChange"></chat-setting-switch>
				</div>
			</div>

			<!-- 群公告编辑弹窗 -->
			<el-dialog class="im-dialog-shell im-dialog-shell--md im-dialog-shell--mobile-center" title="编辑群公告" :visible.sync="noticeDialogVisible" :width="sideDialogWidth"
				:before-close="closeNoticeDialog" :close-on-click-modal="false"
				:close-on-press-escape="false" custom-class="group-side-dialog-panel" append-to-body>
				<div class="notice-dialog-content">
					<el-input v-model="noticeValue" type="textarea" placeholder="填写群公告" maxlength="512" show-word-limit
						:rows="noticeRows" ref="noticeDialogInput" resize="none">
					</el-input>
				</div>
				<div slot="footer" class="dialog-footer">
					<el-button @click="cancelNoticeEdit">取消</el-button>
					<el-button type="primary" @click="saveNotice">保存</el-button>
				</div>
			</el-dialog>

			<div v-show="!group.quit" class="btn-group">
				<div class="action-grid">
					<div class="text-btn" :class="{ 'is-disabled': pendingAction || groupLoading }" @click="onCleanMessage">
						<i class="el-icon-delete"></i>
						清空记录
					</div>
					<div class="text-btn" :class="{ 'is-disabled': pendingAction || groupLoading }" v-show="isOwner" @click="onTransferOwner()">
						<i class="el-icon-right"></i>
						转让群主
					</div>
					<div class="text-btn text-btn--danger" :class="{ 'is-disabled': pendingAction || groupLoading }" v-show="!isOwner" @click="onQuit()">
						<i class="el-icon-switch-button"></i>
						退出群聊
					</div>
				</div>
			</div>
			<!-- 转让群主对话框 -->
			<el-dialog class="im-dialog-shell im-dialog-shell--md im-dialog-shell--mobile-center" title="转让群主" :visible.sync="showTransferDialog" :width="sideDialogWidth"
				:before-close="closeTransferDialog" custom-class="group-side-dialog-panel"
				append-to-body>
				<div class="transfer-tip">
					<i class="el-icon-warning"></i>
					<span>转让后将失去群主权限</span>
				</div>
				<div class="member-selector">
					<div class="selector-label">新群主</div>
					<el-select v-model="transferTargetUserId" placeholder="请选择成员" filterable>
						<el-option v-for="member in transferableMembers" :key="member.userId"
							:label="member.showNickName" :value="member.userId">
							<div class="member-option">
								<span>{{ member.showNickName }}</span>
								<el-tag v-if="member.isManager" size="mini" type="primary">管理员</el-tag>
							</div>
						</el-option>
					</el-select>
				</div>
				<span slot="footer" class="dialog-footer">
					<el-button @click="closeTransferDialog">取消</el-button>
					<el-button type="primary" @click="confirmTransfer" :disabled="!transferTargetUserId">确定</el-button>
				</span>
			</el-dialog>
			<div class="complaint-tip" :class="{ 'is-disabled': pendingAction || groupLoading }" @click="onComplaint">
				<i class="el-icon-warning-outline"></i>
				<span>举报该群</span>
			</div>
		</div>

		<div v-else class="side-empty-state">
			<empty-state
				icon="el-icon-chat-dot-round"
				:title="groupLoadError ? '群资料读取失败' : '群资料加载中'"
				:hint="groupLoadError ? groupLoadErrorMessage : '群成员、权限和公告会在这里展示'"
			></empty-state>
			<el-button v-if="groupLoadError" type="primary" plain size="mini" icon="el-icon-refresh"
				@click="retryGroupLoad">重试</el-button>
		</div>

		<complaint ref="complaint"></complaint>
	</div>
</template>

<script>
import AddGroupMember from '../group/AddGroupMember.vue';
import GroupMemberItem from '../group/GroupMemberItem.vue';
import GroupMemberSelector from '../group/GroupMemberSelector.vue';
import ChatGroupMember from './ChatGroupMember.vue';
import Complaint from '../common/Complaint.vue';
import FileUpload from '../common/FileUpload.vue';
import HeadImage from '../common/HeadImage.vue';
import ChatSettingSwitch from './ChatSettingSwitch.vue';
import { resolveGroupChatDisplayName } from '../../utils/identityDisplay.js';

function normalizeMemberSelection(selection) {
	if (selection && !Array.isArray(selection) && typeof selection === 'object' && selection.selectionMode === 'ALL') {
		return {
			selectionMode: 'ALL',
			userIds: [],
			excludedUserIds: Array.isArray(selection.excludedUserIds) ? selection.excludedUserIds : [],
			count: Number(selection.count) || 0
		};
	}
	const members = Array.isArray(selection) ? selection : [];
	return {
		selectionMode: 'IDS',
		userIds: members
			.filter(member => member && member.userId !== undefined && member.userId !== null)
			.map(member => member.userId),
		excludedUserIds: [],
		count: members.length
	};
}

export default {
	name: "chatGroupSide",
	components: {
		AddGroupMember,
		GroupMemberItem,
		GroupMemberSelector,
		ChatGroupMember,
		Complaint,
		FileUpload,
		HeadImage,
		ChatSettingSwitch
	},
	data() {
		return {
			editing: false,
			showAllMembers: false,
			group: {},
			maxSize: 5 * 1024 * 1024,
			// 编辑状态
			isEditingGroupName: false,
			isEditingRemark: false,
			isEditingNickName: false,
			// 弹窗状态
			noticeDialogVisible: false,
			showTransferDialog: false,
			// 编辑值
			groupNameValue: '',
			noticeValue: '',
			remarkValue: '',
			nickNameValue: '',
			// 转让群主
			transferTargetUserId: null,
			isDnd: false,
			isTop: false,
			groupLoading: false,
			groupLoadError: false,
			groupLoadErrorMessage: '',
			groupLoadSeq: 0,
			pendingAction: '',
			pendingSettingKey: ''
		}
	},
	props: {
		groupId: {
			type: Number
		},
		groupMembers: {
			type: Array
		},
		chat: {
			type: Object
		}
	},
	methods: {
		conversationKey() {
			return this.groupId ? { type: 'GROUP', targetId: this.groupId } : null;
		},
		syncConversationSettings() {
			const key = this.conversationKey();
			this.isDnd = key ? this.chatStore.isDnd(key) : false;
			this.isTop = key ? this.chatStore.isTop(key) : false;
		},
		async runPendingAction(actionKey, handler) {
			if (this.isInteractionLocked) {
				return false;
			}
			this.pendingAction = actionKey;
			try {
				await handler();
				return true;
			} finally {
				if (this.pendingAction === actionKey) {
					this.pendingAction = '';
				}
			}
		},
		async runSettingAction(settingKey, handler) {
			if (this.settingsDisabled || this.pendingSettingKey) {
				return false;
			}
			this.pendingSettingKey = settingKey;
			try {
				await handler();
				return true;
			} finally {
				if (this.pendingSettingKey === settingKey) {
					this.pendingSettingKey = '';
				}
			}
		},
		syncGroupFromStore(group) {
			if (!group) {
				return;
			}
			const nextGroup = JSON.parse(JSON.stringify(group));
			if (this.isEditingGroupName) {
				nextGroup.name = this.group.name;
			}
			if (this.isEditingRemark) {
				nextGroup.remarkGroupName = this.group.remarkGroupName;
			}
			if (this.isEditingNickName) {
				nextGroup.remarkNickName = this.group.remarkNickName;
			}
			this.group = nextGroup;
		},
		async ensureGroupLoaded(groupId = this.groupId) {
			if (!groupId) {
				this.group = {};
				this.groupLoadError = false;
				this.groupLoadErrorMessage = '';
				return null;
			}
			if (this.storeGroup && this.storeGroup.id) {
				this.syncGroupFromStore(this.storeGroup);
			}
			const seq = this.groupLoadSeq + 1;
			this.groupLoadSeq = seq;
			this.groupLoading = true;
			this.groupLoadError = false;
			this.groupLoadErrorMessage = '';
			try {
				const group = await this.groupStore.loadGroupInfo(groupId);
				if (seq !== this.groupLoadSeq || String(groupId) !== String(this.groupId)) {
					return group;
				}
				if (group) {
					this.syncGroupFromStore(group);
					this.chatStore.updateChatFromGroup(group);
				}
				return group;
			} catch (error) {
				if (seq === this.groupLoadSeq) {
					this.groupLoadError = true;
					this.groupLoadErrorMessage = '群资料读取失败，请检查网络后重试';
					console.warn('群资料侧栏读取失败', { groupId, error });
				}
				return null;
			} finally {
				if (seq === this.groupLoadSeq) {
					this.groupLoading = false;
				}
			}
		},
		retryGroupLoad() {
			if (this.groupLoading) {
				return Promise.resolve(null);
			}
			this.$emit('reload');
			return this.ensureGroupLoaded(this.groupId);
		},
		onClose() {
			this.$emit('close');
		},
		onUploadSuccess(data) {
			this.group.headImage = data.originUrl;
			this.group.headImageThumb = data.thumbUrl;
			this.saveGroupInfo();
		},

		// 免打扰开关变化
			onDndChange(value) {
				if (!this.hasGroup || this.isInteractionLocked || this.isEditingGroupProfile) {
				return;
			}
			const data = {
				groupId: this.groupId,
				isDnd: value
			};
				this.runSettingAction('dnd', async () => {
				await this.$http({
					url: '/group/dnd',
					method: 'PUT',
					data: data
				});
				this.chatStore.setDnd(this.conversationKey(), value);
				this.$message.success(value ? '已开启免打扰' : '已关闭免打扰');
			}).catch(() => {
				this.$message.error('操作失败，请重试');
				this.isDnd = !value;
			});
		},

		// 置顶开关变化
			onTopChange(value) {
				if (!this.hasGroup || this.isInteractionLocked || this.isEditingGroupProfile) {
				return;
			}
			const data = {
				groupId: this.groupId,
				isTop: value
			};
				this.runSettingAction('top', async () => {
				await this.$http({
					url: '/group/top',
					method: 'PUT',
					data: data
				});
				this.chatStore.setTop(this.conversationKey(), value);
				this.$message.success(value ? '已置顶聊天' : '已取消置顶');
			}).catch(() => {
				this.$message.error('操作失败，请重试');
				this.isTop = !value;
			});
		},
		onInvite() {
			if (this.isInteractionLocked) {
				return;
			}
			this.$refs.addGroupMember.open();
		},
		onRemove() {
			if (this.isInteractionLocked) {
				return;
			}
			this.$refs.removeSelector.open(-1, [], [], [], { action: 'remove' });
		},
		onRemoveComplete(selection) {
			const payload = normalizeMemberSelection(selection);
			if (payload.count <= 0) {
				return;
			}
			this.runPendingAction('member-op', async () => {
				await this.$http({
					url: "/group/members/remove",
					method: 'delete',
					data: {
						groupId: this.group.id,
						selectionMode: payload.selectionMode,
						userIds: payload.userIds,
						excludedUserIds: payload.excludedUserIds
					}
				});
				this.finishMemberBatchResult({
					totalCount: payload.count,
					successCount: payload.count,
					successText: (count) => `您移除了${count}位成员`,
					partialPrefix: '部分移除成功',
					failPrefix: '移除失败',
					failReason: ''
				});
			}).catch((error) => {
				this.$message.error(`移除失败${this.resolveRequestErrorMessage(error) ? `：${this.resolveRequestErrorMessage(error)}` : ''}`);
			});
		},
		onMuted() {
			if (this.isInteractionLocked) {
				return;
			}
			this.$refs.mutedSelector.open(-1, [], [], [], { action: 'mute' });
		},
		onMutedComplete(selection) {
			this.sendMemberMuted(selection, true);
		},
		onUnmuted() {
			if (this.isInteractionLocked) {
				return;
			}
			this.$refs.unmutedSelector.open(-1, [], [], [], { action: 'unmute' });
		},
		onUnmutedComplete(selection) {
			this.sendMemberMuted(selection, false);
		},
			onAllMutedChange() {
				if (!this.hasGroup || this.isInteractionLocked || this.isEditingGroupProfile) {
				return;
			}
			let data = {
				id: this.group.id,
				isMuted: this.group.isAllMuted
			}
				this.runSettingAction('allMuted', () => this.$http({
				url: '/group/muted',
				method: 'PUT',
				data: data
			})).catch(() => {
				this.$message.error('设置失败，请重试');
				this.group.isAllMuted = !this.group.isAllMuted;
			});
		},
			onAllowInviteChange() {
				if (!this.hasGroup || this.isInteractionLocked || this.isEditingGroupProfile) {
				return;
			}
			let data = {
				groupId: this.group.id,
				isAllowInvite: this.group.isAllowInvite
			}
				this.runSettingAction('allowInvite', () => this.$http({
				url: '/group/allowInvite',
				method: 'PUT',
				data: data
			})).catch(() => {
				this.$message.error('设置失败，请重试');
				this.group.isAllowInvite = !this.group.isAllowInvite;
			});
		},
			onAllowShareCardChange() {
				if (!this.hasGroup || this.isInteractionLocked || this.isEditingGroupProfile) {
				return;
			}
			let data = {
				groupId: this.group.id,
				isAllowShareCard: this.group.isAllowShareCard
			}
				this.runSettingAction('allowShareCard', () => this.$http({
				url: '/group/allowShareCard',
				method: 'PUT',
				data: data
			})).catch(() => {
				this.$message.error('设置失败，请重试');
				this.group.isAllowShareCard = !this.group.isAllowShareCard;
			});
		},
		// 群组权限配置变更
			onViewMemberProfileChange() {
				this.updateGroupPermissions({
					isAllowViewMemberProfile: this.group.isAllowViewMemberProfile
				}, '成员资料可见性', 'viewMemberProfile');
		},
			onViewOnlineCountChange() {
				this.updateGroupPermissions({
					isAllowViewOnlineCount: this.group.isAllowViewOnlineCount
				}, '在线人数可见性', 'viewOnlineCount');
		},
			onViewHistoryChange() {
				this.updateGroupPermissions({
					isAllowViewHistory: this.group.isAllowViewHistory
				}, '历史记录可见性', 'viewHistory');
			},
			updateGroupPermissions(permissions, permissionName, settingKey) {
				if (!this.hasGroup || this.isInteractionLocked || this.isEditingGroupProfile) {
				return;
			}
			const data = {
				groupId: this.group.id,
				...permissions
			};
				this.runSettingAction(settingKey, async () => {
				await this.$http({
					url: `/group/permissions/${this.group.id}`,
					method: 'PUT',
					data: data
				});
				this.$message.success(`${permissionName}设置已更新`);
				this.groupStore.updateGroupPermissions(this.group.id, permissions);
			}).catch(() => {
				Object.keys(permissions).forEach(key => {
					this.group[key] = !this.group[key];
				});
				this.$message.error('设置失败，请重试');
			});
		},
			onShowMoreMember() {
				if (this.isInteractionLocked) {
					return;
				}
				this.showAllMembers = true;
				if (!this.group || !this.group.id) {
					return;
				}
				this.groupStore.refreshMember(this.group.id, {
					refreshOnline: false,
					pageLimit: 30
				}).catch(() => []);
			},
		onQuit() {
			if (!this.hasGroup || this.isInteractionLocked) {
				return;
			}
			this.$confirm('退出群聊后将不再接受群里的消息，确认退出吗？', '确认退出?', {
				confirmButtonText: '确定',
				cancelButtonText: '取消',
				type: 'warning'
			}).then(() => {
				this.$http({
					url: `/group/quit/${this.group.id}`,
					method: 'delete'
				}).then(() => {
					this.groupStore.removeGroup(this.group.id);
					this.chatStore.removeGroupChat(this.group.id);
				});
			})
		},
		onTransferOwner() {
			if (this.isInteractionLocked) {
				return;
			}
			this.transferTargetUserId = null;
			this.showTransferDialog = true;
		},
		confirmTransfer() {
			if (!this.transferTargetUserId) {
				this.$message.warning('请选择新群主');
				return;
			}

			const targetMember = this.findGroupMember(this.transferTargetUserId);
			const targetName = targetMember ? targetMember.showNickName : '该成员';

			this.$confirm(`确定将群主转让给 ${targetName} 吗？转让后您将失去群主身份。`, '确认转让', {
				confirmButtonText: '确定',
				cancelButtonText: '取消',
				type: 'warning'
			}).then(() => {
				const data = {
					groupId: this.group.id,
					newOwnerId: this.transferTargetUserId
				};
				this.$http({
					url: '/group/transfer',
					method: 'PUT',
					data: data
				}).then(() => {
					this.$message.success('群主转让成功');
					this.showTransferDialog = false;
					// 重新加载群组信息
					this.$emit('reload');
				}).catch(() => {
					this.$message.error('转让失败，请重试');
				});
			}).catch(() => {
				// 用户取消
			});
		},
		sendMemberMuted(selection, isMuted) {
			const payload = normalizeMemberSelection(selection);
			if (payload.count <= 0) {
				return;
			}
			this.runPendingAction('member-op', async () => {
				await this.$http({
					url: "/group/members/muted",
					method: "put",
					data: {
						groupId: this.group.id,
						selectionMode: payload.selectionMode,
						userIds: payload.userIds,
						excludedUserIds: payload.excludedUserIds,
						isMuted: Boolean(isMuted)
					}
				});
				const actionLabel = isMuted ? '禁言' : '解除禁言';
				this.finishMemberBatchResult({
					totalCount: payload.count,
					successCount: payload.count,
					successText: (count) => isMuted ? `您对${count}位成员进行了禁言` : `您解除了${count}位成员的禁言`,
					partialPrefix: `部分${actionLabel}成功`,
					failPrefix: `${actionLabel}失败`,
					failReason: ''
				});
			}).catch((error) => {
				const actionLabel = isMuted ? '禁言' : '解除禁言';
				const reason = this.resolveRequestErrorMessage(error);
				this.$message.error(`${actionLabel}失败${reason ? `：${reason}` : ''}`);
			});
		},
		resolveRequestErrorMessage(error) {
			if (error && typeof error.message === 'string' && error.message) {
				return error.message;
			}
			if (error && typeof error.msg === 'string' && error.msg) {
				return error.msg;
			}
			if (error && error.response && error.response.data) {
				const payload = error.response.data;
				if (typeof payload === 'string' && payload) {
					return payload;
				}
				if (payload && typeof payload.message === 'string' && payload.message) {
					return payload.message;
				}
			}
			return '';
		},
		finishMemberBatchResult({ totalCount, successCount, successText, partialPrefix, failPrefix, failReason }) {
			const total = Number(totalCount) || 0;
			const success = Number(successCount) || 0;
			if (success > 0) {
				this.$emit('reload');
			}
			if (success === total && total > 0) {
				this.$message.success(typeof successText === 'function' ? successText(success) : `操作成功：${success}`);
				return;
			}
			if (success > 0) {
				const failed = Math.max(0, total - success);
				const reasonText = failReason ? `，原因：${failReason}` : '';
				this.$message.warning(`${partialPrefix}：成功${success}人，失败${failed}人${reasonText}`);
				return;
			}
			const failText = failReason ? `：${failReason}` : '';
			this.$message.error(`${failPrefix}${failText}`);
		},

		// 开始编辑群聊名称
		startEditGroupName() {
			if ((!this.isOwner && !this.isManager) || this.isInteractionLocked) return;
			this.isEditingGroupName = true;
			this.groupNameValue = this.group.name || '';
			this.$nextTick(() => {
				this.$refs.groupNameInput.focus();
			});
		},

		// 保存群聊名称
		saveGroupName() {
			const newName = this.groupNameValue.trim();
			if (newName === (this.group.name || '')) {
				this.isEditingGroupName = false;
				return;
			}
			this.group.name = newName;
			this.saveGroupInfo();
		},

		// 打开群公告编辑弹窗
		openNoticeDialog() {
			if ((!this.isOwner && !this.isManager) || this.isInteractionLocked) return;
			this.noticeValue = this.group.notice || '';
			this.noticeDialogVisible = true;
			this.$nextTick(() => {
				this.$refs.noticeDialogInput.focus();
			});
		},

		// 保存群公告
		saveNotice() {
			const newNotice = this.noticeValue.trim();
			if (newNotice === (this.group.notice || '')) {
				this.noticeDialogVisible = false;
				return;
			}
			this.group.notice = newNotice;
			this.saveGroupInfo();
		},

		// 取消群公告编辑
		cancelNoticeEdit() {
			this.noticeDialogVisible = false;
			this.noticeValue = this.group.notice || '';
		},
		closeNoticeDialog() {
			this.noticeDialogVisible = false;
		},
		closeTransferDialog() {
			this.showTransferDialog = false;
		},

		// 开始编辑备注
		startEditRemark() {
			if (this.isInteractionLocked) {
				return;
			}
			this.isEditingRemark = true;
			this.remarkValue = this.group.remarkGroupName || '';
			this.$nextTick(() => {
				this.$refs.remarkInput.focus();
			});
		},

		// 保存备注
		saveRemark() {
			const newRemark = this.remarkValue.trim();
			if (newRemark === (this.group.remarkGroupName || '')) {
				this.isEditingRemark = false;
				return;
			}
			this.group.remarkGroupName = newRemark;
			this.saveGroupInfo();
		},

		// 开始编辑昵称
		startEditNickName() {
			if (this.isInteractionLocked) {
				return;
			}
			this.isEditingNickName = true;
			this.nickNameValue = this.group.remarkNickName || '';
			this.$nextTick(() => {
				this.$refs.nickNameInput.focus();
			});
		},

		// 保存昵称
		saveNickName() {
			const newNickName = this.nickNameValue.trim();
			if (newNickName === (this.group.remarkNickName || '')) {
				this.isEditingNickName = false;
				return;
			}
			this.group.remarkNickName = newNickName;
			this.saveGroupInfo();
		},

		// 保存群信息
		saveGroupInfo() {
			if (!this.hasGroup) {
				return;
			}
			let vo = this.group;
			this.runPendingAction('profile', async () => {
				const group = await this.$http({
					url: "/group/modify",
					method: "put",
					data: vo
				});
				this.groupStore.updateGroup(group);
				this.chatStore.updateChatFromGroup(group)
				this.group = JSON.parse(JSON.stringify(group));
				this.$message.success("修改成功");
				if (this.noticeDialogVisible) {
					this.noticeDialogVisible = false;
				}
			}).catch(() => {
				this.$message.error('保存群资料失败，请重试');
			}).finally(() => {
				this.isEditingGroupName = false;
				this.isEditingRemark = false;
				this.isEditingNickName = false;
			});
		},
		// 清空聊天记录
		onCleanMessage() {
			if (!this.hasGroup || this.isInteractionLocked) {
				return;
			}
			this.$confirm(`确定要清空'${this.group.name}'的所有聊天记录吗？`, '清空聊天记录', {
				confirmButtonText: '确定',
				cancelButtonText: '取消',
				type: 'warning'
			}).then(() => {
				this.chatStore.cleanMessage(this.chat);
				this.$message.success(`您清空了'${this.group.name}'的聊天记录`);
			}).catch(() => {
				// 用户取消操作
			});
		},
		// 投诉群组
		onComplaint() {
			if (!this.hasGroup || this.isInteractionLocked) {
				return;
			}
			this.$refs.complaint.open(2, this.group.id, this.group.name);
		},
		findGroupMember(userId) {
			if (!this.group || !this.group.id || userId === undefined || userId === null) {
				return null;
			}
			return this.groupStore.findGroupMember(this.group.id, userId);
		}
	},
	computed: {
		hasGroup() {
			return Boolean(this.group && this.group.id);
		},
		isInteractionLocked() {
			return Boolean(this.groupLoading || this.pendingAction || this.pendingSettingKey);
		},
		settingsDisabled() {
			return Boolean(this.groupLoading || this.pendingAction || this.isEditingGroupProfile);
		},
		isEditingGroupProfile() {
			return Boolean(this.isEditingGroupName || this.isEditingRemark || this.isEditingNickName ||
				this.noticeDialogVisible);
		},
		storeGroup() {
			return this.groupStore.findGroup(this.groupId) || null;
		},
		resolvedGroupMembers() {
			if (this.storeGroup && Array.isArray(this.storeGroup.members)) {
				return this.storeGroup.members;
			}
			return Array.isArray(this.groupMembers) ? this.groupMembers : [];
		},
		mine() {
			return this.userStore.userInfo;
		},
		groupDisplayName() {
			return resolveGroupChatDisplayName(this.group) || '群聊';
		},
		roleLabel() {
			if (this.isOwner) {
				return '群主';
			}
			if (this.isManager) {
				return '管理员';
			}
			return '成员';
		},
		isOwner() {
			return this.group.ownerId == this.mine.id;
		},
		isManager() {
			if (!this.group || !this.group.id) {
				return false;
			}
			return this.groupStore.isManager(this.group.id, this.mine.id);
		},
		isAllowInvite() {
			return this.isOwner || this.isManager || this.group.isAllowInvite;
		},
		canEditGroupProfile() {
			return this.isOwner || this.isManager;
		},
		imageAction() {
			return `/image/upload?thumbSize=20`;
		},
		memberRuntimeState() {
			const runtimeState = {
				activeMembers: [],
				activeMemberCount: 0,
				activeOnlineCount: 0,
				managerIds: [],
				mutedMemberIds: [],
				unmutedMemberIds: [],
				transferableMembers: []
			};
			this.resolvedGroupMembers.forEach((member) => {
				if (!member || member.quit) {
					return;
				}
				runtimeState.activeMembers.push(member);
				runtimeState.activeMemberCount++;
				if (member.online) {
					runtimeState.activeOnlineCount++;
				}
				if (member.isManager) {
					runtimeState.managerIds.push(member.userId);
				}
				if (member.isMuted) {
					runtimeState.mutedMemberIds.push(member.userId);
				} else {
					runtimeState.unmutedMemberIds.push(member.userId);
				}
				if (member.userId !== this.mine.id) {
					runtimeState.transferableMembers.push(member);
				}
			});
			return runtimeState;
		},
		managerIds() {
			return this.memberRuntimeState.managerIds;
		},
		showMembers() {
			return this.memberRuntimeState.activeMembers;
		},
		memberTotalCount() {
			const candidates = [
				this.group && this.group.memberCount,
				this.storeGroup && this.storeGroup.memberCount,
				this.storeGroup && this.storeGroup.memberPage && this.storeGroup.memberPage.total
			];
			for (const value of candidates) {
				const count = Number(value);
				if (Number.isFinite(count) && count >= 0) {
					return Math.floor(count);
				}
			}
			return null;
		},
		memberCount() {
			return this.memberTotalCount === null ? 0 : this.memberTotalCount;
		},
		onlineCount() {
			const activeOnlineMembers = this.memberRuntimeState.activeOnlineCount;
			if (activeOnlineMembers > 0) {
				return activeOnlineMembers;
			}
			return this.group.onlineCount || 0;
		},
		canViewOnlineCount() {
			return this.isOwner || this.isManager || this.group.isAllowViewOnlineCount;
		},
		groupSummaryText() {
			if (this.memberTotalCount === null) {
				return '群资料加载中';
			}
			return `${this.memberTotalCount} 人`;
		},
		showGridMembers() {
			return this.showMembers.slice(0, this.showMaxIdx);
		},
		showMaxIdx() {
			let idx = 12;
			if (this.isAllowInvite) {
				// 邀请按钮
				idx--;
			}
			if (this.isOwner || this.isManager) {
				// 开启禁言、解除禁言、移除成员
				idx -= 3;
			}
			return idx;
		},
		transferableMembers() {
			return this.memberRuntimeState.transferableMembers;
		},
		isMobileDialog() {
			return this.configStore.isMobile;
		},
		sideDialogWidth() {
			return this.isMobileDialog ? '100%' : '480px';
		},
		noticeRows() {
			return this.isMobileDialog ? 4 : 5;
		},
		conversationStateVersion() {
			return this.chatStore.conversationStateVersion;
		}
	},
	watch: {
		storeGroup: {
			handler(group) {
				this.syncGroupFromStore(group);
			},
			deep: true,
			immediate: true
		},
		conversationStateVersion: {
			handler() {
				this.syncConversationSettings();
			},
			immediate: true
		},
		groupId: {
			handler(groupId) {
				this.group = {};
				this.groupLoadError = false;
				this.groupLoadErrorMessage = '';
				this.ensureGroupLoaded(groupId);
			},
			immediate: true
		}
	}
}
</script>

<style lang="scss">
.chat-group-side {
	--side-card-gap: 8px;
	--side-field-height: 36px;
	--side-field-radius: 9px;
	padding: 10px 12px 12px;
	position: relative;
	height: 100%;
	overflow-y: auto;
	scrollbar-gutter: stable;
	box-sizing: border-box;
	background: var(--im-chat-bg);

	// 公共卡片样式
	%card-style {
		padding: 10px 12px;
		background: transparent;
		border-radius: 9px;
		border: 1px solid var(--im-surface-panel-border);
		box-shadow: none;
	}

	.side-empty-state {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		height: 100%;

		.empty-state-view {
			flex: 0 1 auto;
		}
	}

	.group-side-content {
		display: flex;
		flex-direction: column;
		gap: var(--side-card-gap);
	}

	.group-overview-card {
		@extend %card-style;
	}

	.group-overview-main {
		display: flex;
		align-items: flex-start;
		gap: 12px;
	}

	.group-overview-avatar {
		flex-shrink: 0;
	}

	.group-avatar-upload {
		position: relative;
		display: inline-flex;
		flex-shrink: 0;
		cursor: pointer;
		border-radius: 14px;
		overflow: hidden;
	}

	.group-avatar-upload__mask {
		position: absolute;
		inset: auto 0 0 0;
		display: flex;
		align-items: center;
		justify-content: center;
		height: 18px;
		background: rgba(var(--im-black-rgb), 0.56);
		color: rgba(var(--im-white-rgb), 0.96);
		font-size: 10px;
		line-height: 1;
	}

	.group-overview-body {
		flex: 1;
		min-width: 0;
		text-align: left;
	}

	.group-overview-subtitle {
		margin-top: 4px;
		font-size: 12px;
		line-height: 1.4;
		color: var(--im-text-color-light);
	}

	.group-overview-status {
		display: flex;
		align-items: center;
		gap: 6px;
		width: 100%;
		height: 20px;
		margin-top: 3px;
		color: var(--im-color-primary);
		font-size: 11px;
		font-weight: 500;
		overflow: hidden;

		&.is-idle {
			visibility: hidden;
		}

		span {
			flex: 1;
			min-width: 0;
			overflow: hidden;
			white-space: nowrap;
			text-overflow: ellipsis;
		}

		button {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			width: 20px;
			height: 20px;
			padding: 0;
			border: 0;
			background: transparent;
			color: inherit;
			cursor: pointer;
		}

		&.is-error {
			color: var(--im-color-warning);
		}
	}

	.group-overview-title-row {
		display: flex;
		align-items: center;
		gap: 8px;
		flex-wrap: nowrap;
		min-width: 0;
	}

	.group-overview-title {
		flex: 1;
		min-width: 0;
		font-size: 15px;
		font-weight: 600;
		line-height: 1.35;
		color: var(--im-text-color);
		white-space: nowrap;
		text-overflow: ellipsis;
		overflow: hidden;
	}

	.group-role-badge {
		display: inline-flex;
		align-items: center;
		flex-shrink: 0;
		height: 20px;
		padding: 0 7px;
		border-radius: 999px;
		background: rgba(var(--im-primary-rgb), 0.08);
		color: var(--im-color-primary);
		font-size: 11px;
		font-weight: 600;
	}

	.member-area {
		margin-bottom: 0;
		@extend %card-style;

		.member-header {
			display: flex;
			align-items: center;
			justify-content: flex-start;
			gap: 12px;
			margin-bottom: 10px;
			padding-bottom: 8px;
			border-bottom: 1px solid var(--im-surface-panel-border);

			.member-title {
				flex: 0 0 auto;
				font-size: 14px;
				font-weight: 600;
				color: var(--im-text-color-primary);
				text-align: left;
			}

			.more-member-btn {
				display: inline-flex;
				align-items: center;
				justify-content: center;
				margin-left: auto;
				flex-shrink: 0;
				gap: 4px;
				font-size: 12px;
				color: var(--im-color-primary);
				cursor: pointer;
				padding: 4px 8px;
				border-radius: 999px;
				background: rgba(var(--im-primary-rgb), 0.08);
				transition: background 0.2s ease;

				&:hover {
					background: rgba(var(--im-primary-rgb), 0.12);
				}
			}
		}

		.member-items {
			display: grid;
			grid-template-columns: repeat(4, minmax(0, 1fr));
			align-items: start;
			text-align: center;
			gap: 10px 8px;

			> .member-entry,
			> .member-tools {
				width: 100%;
				min-width: 0;
			}

			.member-item {
				width: 100%;
			}

			.member-tools {
				display: flex;
				flex-direction: column;
				align-items: center;
				justify-self: stretch;
				width: 100%;

				&.is-disabled {
					pointer-events: none;
					opacity: 0.6;
				}

				.tool-btn {
					width: 42px;
					height: 42px;
					display: flex;
					align-items: center;
					justify-content: center;
					border: var(--im-border);
					border-radius: 10px;
					font-size: 18px;
					cursor: pointer;
					color: var(--im-text-color-light);
					box-sizing: border-box;
					font-weight: 800;
					background: var(--im-control-soft-bg);
					transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
					box-shadow: none;

					&:hover {
						border-color: var(--im-color-primary-light-2);
						color: var(--im-color-primary-light-2);
						background: rgba(var(--im-primary-rgb), 0.06);
					}
				}

				.tool-text {
					font-size: 11px;
					color: var(--im-text-color-light);
					margin-top: 5px;
					text-align: center;
					font-weight: 500;
					width: 100%;
					height: auto;
					line-height: 1.3;
					white-space: nowrap;
					text-overflow: ellipsis;
					overflow: hidden;
				}
			}
		}
	}

	.switch-setting {
		margin-bottom: 0;
		padding: 4px 6px;
		@extend %card-style;

		.switch-item {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: 10px;
			min-height: 38px;
			padding: 7px 8px;
			border-bottom: 1px solid var(--im-surface-panel-border);
			transition: background 0.2s ease;
			border-radius: 7px;

			&:last-child {
				border-bottom: none;
			}

			&:hover {
				background: rgba(var(--im-primary-rgb), 0.04);
			}

			.label {
				display: flex;
				align-items: center;
				gap: 8px;
				font-size: 13px;
				font-weight: 500;
				color: var(--im-text-color-primary);

				i {
					width: 16px;
					text-align: center;
					font-size: 14px;
					color: var(--im-color-primary);
				}
			}

		}
	}

	.group-info-section {
		margin-bottom: 0;
		@extend %card-style;

		.info-item {
			display: flex;
			flex-direction: column;
			align-items: stretch;
			gap: 6px;
			padding: 0;
			border-bottom: none;
			border-radius: 0;

			& + .info-item {
				margin-top: 10px;
			}

			&:hover {
				background: transparent;
			}

			.info-label {
				display: flex;
				align-items: center;
				gap: 8px;
				font-weight: 500;
				color: var(--im-text-color-primary);
				font-size: 13px;
				min-width: 0;

				i {
					width: 16px;
					text-align: center;
					font-size: 14px;
					color: var(--im-color-primary);
				}
			}

			.info-content {
				display: flex;
				align-items: center;
				min-height: var(--side-field-height);
				min-width: 0;
				width: 100%;

				.el-input {
					width: 100%;
					display: block;
				}

				.el-input__inner {
					height: var(--side-field-height);
					min-height: var(--side-field-height);
					line-height: calc(var(--side-field-height) - 2px);
					padding: 0 10px;
					border-radius: var(--side-field-radius);
					border-color: var(--im-control-border);
					font-size: 14px;
					box-sizing: border-box;
				}

				.el-input__inner:focus {
					border-color: rgba(var(--im-primary-rgb), 0.38);
					box-shadow: none;
				}

					.info-display {
						display: flex;
						align-items: center;
						justify-content: flex-start;
						gap: 6px;
						padding: 0 10px;
					cursor: pointer;
					transition: background 0.2s ease;
					min-height: var(--side-field-height);
					width: 100%;
					border-radius: var(--side-field-radius);
					background: var(--im-control-soft-bg);
					box-sizing: border-box;

					&:hover {
						background: var(--im-control-soft-bg-hover);
					}

						span {
							flex: 1;
							display: block;
							font-size: 14px;
							line-height: 1.45;
							color: var(--im-text-color);
							text-align: left;
							min-width: 0;
							overflow: hidden;
							white-space: nowrap;
							text-overflow: ellipsis;

							&.info-placeholder {
								display: inline-flex;
								align-items: center;
								color: var(--im-text-color-light);
								font-style: normal;
							}
					}

					i {
						margin-left: auto;
						font-size: 12px;
						color: var(--im-text-color-light);
						opacity: 0.65;
					}

					&.info-display--readonly {
						cursor: default;
						background: var(--im-control-soft-bg);

						&:hover {
							background: var(--im-control-soft-bg);
						}
					}
				}
			}
		}
	}

	.notice-section {
		margin-bottom: 0;
		@extend %card-style;

		.notice-header {
			display: flex;
			align-items: center;
			gap: 8px;
			margin-bottom: 8px;
			font-weight: 500;
			color: var(--im-text-color-primary);
			font-size: 13px;

			i {
				font-size: 14px;
				color: var(--im-color-primary);
			}
		}

		.notice-content {

			.notice-display {
				min-height: 44px;
				padding: 10px;
				border-radius: var(--side-field-radius);
				cursor: pointer;
				transition: background 0.2s ease;
				position: relative;
				background: var(--im-control-soft-bg);

				&:hover {
					background: var(--im-control-soft-bg-hover);
				}

				.notice-text {
					font-size: 13px;
					color: var(--im-text-color);
					line-height: 1.45;
					word-break: break-word;
					text-align: left;
					display: -webkit-box;
					-webkit-line-clamp: 2;
					-webkit-box-orient: vertical;
					overflow: hidden;
				}

				.notice-placeholder {
					font-size: 13px;
					color: var(--im-text-color-light);
					line-height: 1.45;
					text-align: left;
				}

				&.notice-display--readonly {
					cursor: default;

					&:hover {
						transform: none;
						background: var(--im-control-soft-bg);
					}
				}
			}
		}
	}

	// 群公告弹窗样式
	.notice-dialog-content {
		padding: 6px 0 2px;

		.el-textarea {
			.el-textarea__inner {
				font-size: 13px;
				padding: 12px;
				border-radius: 6px;
				border: 1px solid var(--im-control-border);
				transition: border-color 0.2s ease;
				min-height: 112px;
				line-height: 1.5;

				&:focus {
					border-color: rgba(var(--im-primary-rgb), 0.5);
					box-shadow: none;
				}
			}
		}
	}

	.dialog-footer {
		text-align: right;

		.el-button {
			margin-left: 10px;
		}
	}

	.btn-group {
		text-align: left;
		@extend %card-style;

		.action-grid {
			display: flex;
			flex-wrap: wrap;
			gap: 8px;
		}

		.text-btn {
			display: flex;
			align-items: center;
			justify-content: center;
			gap: 4px;
			flex: 1 1 calc(50% - 4px);
			min-width: 0;
			height: 34px;
			padding: 0 10px;
			font-size: 13px;
			font-weight: 500;
			cursor: pointer;
			transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
			border-radius: 7px;
			color: var(--im-text-color);
			background: var(--im-control-soft-bg);
			border: 1px solid var(--im-control-border);
			box-sizing: border-box;

			&:hover {
				color: var(--im-color-primary);
				background: rgba(var(--im-primary-rgb), 0.08);
				border-color: rgba(var(--im-primary-rgb), 0.16);
			}

			&.is-disabled {
				cursor: not-allowed;
				opacity: 0.6;
				pointer-events: none;
			}

			i {
				font-size: 13px;
			}

			&.text-btn--danger {
				color: var(--im-color-danger);
				background: rgba(var(--im-danger-rgb), 0.06);
				border-color: rgba(var(--im-danger-rgb), 0.12);

				&:hover {
					color: var(--im-color-danger);
					background: rgba(var(--im-danger-rgb), 0.11);
					border-color: rgba(var(--im-danger-rgb), 0.2);
				}
			}
		}
	}

	// 转让群主对话框样式
	.transfer-tip {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 9px 10px;
		margin-bottom: 10px;
		background: rgba(var(--im-warning-rgb), 0.1);
		border: 1px solid rgba(var(--im-warning-rgb), 0.2);
		border-radius: 6px;
		color: var(--im-color-warning);
		font-size: 12px;

		i {
			font-size: 16px;
		}
	}

	.member-selector {
		margin-bottom: 0;

		.selector-label {
			margin-bottom: 8px;
			font-size: 12px;
			font-weight: 500;
			color: var(--im-text-color-light);
		}

		.el-select {
			width: 100%;
		}

		.member-option {
			display: flex;
			align-items: center;
			justify-content: space-between;
			width: 100%;
		}
	}

	.complaint-tip {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 6px;
		min-height: 34px;
		padding: 0 12px;
		font-size: 13px;
		color: var(--im-text-color-light);
		cursor: pointer;
		margin-top: 0;
		background: var(--im-control-soft-bg);
		border: 1px solid var(--im-control-border);
		border-radius: 9px;
		transition: color 0.2s ease, background 0.2s ease, border-color 0.2s ease;

		&:hover {
			color: var(--im-color-primary);
			background: rgba(var(--im-primary-rgb), 0.08);
			border-color: rgba(var(--im-primary-rgb), 0.14);
		}

		&.is-disabled {
			cursor: not-allowed;
			opacity: 0.6;
			pointer-events: none;
		}

		i {
			font-size: 13px;
		}
	}
}

.group-side-dialog-panel {
	max-height: calc(100vh - 32px);
	display: flex;
	flex-direction: column;
	overflow: hidden;

	&:not(.is-fullscreen) {
		width: min(480px, calc(100vw - 24px)) !important;
	}

	.el-dialog__body {
		flex: 1;
		min-height: 0;
		overflow: auto;
		padding: 14px 16px 10px;
	}

	.el-dialog__footer {
		flex-shrink: 0;
		padding: 0 16px 16px;
	}

	&.is-fullscreen {
		max-height: none;

		.el-dialog__body {
			padding: 12px;
		}
	}
}

@media (max-width: 1280px) {
	.chat-group-side {
		.member-area {
			.member-header {
				align-items: center;
				flex-direction: row;
				gap: 12px;
			}

			.member-items {
				grid-template-columns: repeat(3, minmax(0, 1fr));
			}
		}
	}
}

@media (max-width: 640px) {
	.chat-group-side {
		padding: 10px;

		.group-side-content {
			gap: var(--side-card-gap);
		}

		.btn-group {
			.text-btn {
				flex-basis: 100%;
			}
		}
	}
}
</style>
